import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { ExternalLink, DollarSign, CreditCard, Calendar } from 'lucide-react';
import CustomAlert from '../components/CustomAlert';
import DateRangeFilter from '../components/DateRangeFilter';
import FiltersPanel from '../components/FiltersPanel';

const apiUrl = process.env.REACT_APP_API_URL;

export default function PaymentsList() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Custom Alert State
  const [alert, setAlert] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    details: ''
  });

  const showAlert = (type, title, message, details = '') => {
    setAlert({ isOpen: true, type, title, message, details });
  };

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, isOpen: false }));
  };

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch(`${apiUrl}/partners?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch partners');
      }

      const partnersData = await response.json();
      setPartners(Array.isArray(partnersData) ? partnersData : []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  const fetchPayments = async (filterParams = {}) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        showAlert('error', 'Authentication Required', 'Please login to view payments.');
        return;
      }

      const queryParams = new URLSearchParams();
      queryParams.append('limit', '1000');
      if (filterParams.search) queryParams.append('search', filterParams.search);
      if (filterParams.partner) queryParams.append('partner', filterParams.partner);
      if (filterParams.dateFrom) queryParams.append('dateFrom', filterParams.dateFrom);
      if (filterParams.dateTo) queryParams.append('dateTo', filterParams.dateTo);

      const response = await fetch(`${apiUrl}/payments/latest?${queryParams.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching payments:', error);
      showAlert('error', 'Error Loading Payments', 'Could not load payments data.', error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
    fetchPayments();
  }, []);

  // Apply filters on backend
  const applyFilters = () => {
    const filterParams = {};
    if (filter) filterParams.search = filter;
    if (partnerFilter) filterParams.partner = partnerFilter;
    if (dateFromFilter) filterParams.dateFrom = dateFromFilter;
    if (dateToFilter) filterParams.dateTo = dateToFilter;

    fetchPayments(filterParams);
  };

  const allColumns = useMemo(() => [
    {
      name: '#',
      cell: (row, index) => (
        <span className="font-semibold text-gray-600">{index + 1}</span>
      ),
      width: '50px',
      sortable: false,
    },
    {
      name: 'Датум',
      selector: (row) => row.payment_date,
      sortable: true,
      width: '120px',
      cell: (row) => row.payment_date ? new Date(row.payment_date).toLocaleDateString('mk-MK') : '-',
    },
    {
      name: 'Фактура',
      selector: (row) => row.invoice_number,
      sortable: true,
      cell: (row) => <span className="font-semibold text-gray-900">#{row.invoice_number}</span>
    },
    {
      name: 'Партнер',
      selector: (row) => row.customer_name,
      sortable: true,
      grow: 2,
    },
    {
      name: 'Износ',
      selector: (row) => row.amount,
      sortable: true,
      right: true,
      width: '140px',
      cell: (row) => <span className="font-semibold text-green-600">+{parseFloat(row.amount || 0).toFixed(2)} ден.</span>
    },
    {
      name: 'Начин на плаќање',
      selector: (row) => row.payment_method,
      sortable: true,
      width: '160px',
      cell: (row) => (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 capitalize">
          {row.payment_method?.replace('_', ' ')}
        </span>
      ),
    },
    {
      name: 'Белешка',
      selector: (row) => row.note || '-',
      sortable: true,
    },
    {
      name: 'Акции',
      cell: (row) => (
        <button
          onClick={() => navigate(`/invoices/view/${row.invoice_id}`)}
          title="Погледни фактура"
          className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <ExternalLink size={18} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '100px',
    },
  ], [navigate]);

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns.map((col) => col.name).filter((name) => ['#', 'Датум', 'Фактура', 'Партнер', 'Износ', 'Начин на плаќање', 'Акции'].includes(name))
  );

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName) ? prev.filter((name) => name !== columnName) : [...prev, columnName]
    );
  };

  const columns = useMemo(() => allColumns.filter((col) => visibleColumns.includes(col.name)), [allColumns, visibleColumns]);

  const paymentStats = useMemo(() => {
    const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const now = new Date();
    const thisMonth = payments.filter((p) => {
      const d = new Date(p.payment_date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return { totalAmount, total: payments.length, thisMonth };
  }, [payments]);

  // Use all partners for dropdown
  const uniquePartners = useMemo(() => {
    if (!Array.isArray(partners)) return [];
    return partners
      .filter(partner => partner.name && partner.name.trim() !== '')
      .map(partner => ({ id: partner.id, name: partner.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [partners]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-lg">Вчитување на плаќања...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-green-50">
              <DollarSign className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Вкупен износ</p>
              <p className="text-lg font-semibold text-green-600">{paymentStats.totalAmount.toFixed(2)} ден.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <CreditCard className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Вкупно плаќања</p>
              <p className="text-lg font-semibold text-gray-900">{paymentStats.total}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Calendar className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Овој месец</p>
              <p className="text-lg font-semibold text-gray-900">{paymentStats.thisMonth}</p>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <FiltersPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            {/* General Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Пребарај</label>
              <input
                type="text"
                placeholder="Фактура, партнер, начин..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Partner Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Партнер</label>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                <option value="">Сите партнери</option>
                {uniquePartners.map((partner) => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <DateRangeFilter
              preset={datePreset}
              setPreset={setDatePreset}
              dateFrom={dateFromFilter}
              setDateFrom={setDateFromFilter}
              dateTo={dateToFilter}
              setDateTo={setDateToFilter}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
            <button
              onClick={() => {
                setFilter('');
                setPartnerFilter('');
                setDateFromFilter('');
                setDateToFilter('');
                setDatePreset('');
                fetchPayments();
              }}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
              title="Избриши ги сите филтери"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Избриши</span>
            </button>
            <button
              onClick={applyFilters}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
              title="Примени филтери"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Филтрирај</span>
            </button>
          </div>
                </FiltersPanel>

        {/* Payments Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">Плаќања</h2>
            <div className="flex gap-2 items-center">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
                  title="Избери колони"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v16M15 4v16" />
                  </svg>
                  <span className="hidden sm:inline">Колони</span>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-2">
                    <div className="p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">Прикажи колони</p>
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {allColumns.map((col) => (
                          <label key={col.name} className="flex items-center cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.name)}
                              onChange={() => toggleColumn(col.name)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0"
                            />
                            <span className="ml-3 text-sm text-gray-700">{col.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
                onClick={applyFilters}
                disabled={loading}
                title="Освежи ја листата"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">{loading ? 'Вчитување...' : 'Освежи'}</span>
              </button>

              <CSVLink
                data={payments}
                filename="payments.csv"
                target="_blank"
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium text-sm transition-colors no-underline flex items-center space-x-1.5"
                title="Експортирај ги плаќањата во CSV"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden sm:inline">Експорт</span>
              </CSVLink>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={payments}
            pagination
            highlightOnHover={false}
            striped={false}
            responsive
            persistTableHead
            noDataComponent={
              <div className="p-12 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a4 4 0 00-8 0v2M5 9h14l1 12H4L5 9z" />
                </svg>
                <p className="text-gray-600">Нема пронајдени плаќања</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Custom Alert Modal */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        details={alert.details}
      />
    </div>
  );
}
